/*
 * Copyright 2020 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
// IMPORTANT
// This is a copy of the Page component from the Backstage core-components package.
// We need to copy it here because the original component do not expose a ref, which
// is needed for us to be able to implement the floating menu.
//
// https://github.com/backstage/backstage/blob/master/packages/core-components/src/layout/Page/Page.tsx
import { ReactNode, forwardRef, useMemo } from 'react';

import { Theme, ThemeProvider, makeStyles } from '@material-ui/core/styles';

import classNames from 'classnames';

const useStyles = makeStyles(
  {
    root: {
      display: 'grid',
      gridTemplateAreas:
        "'pageHeader pageHeader pageHeader' 'pageSubheader pageSubheader pageSubheader' 'pageNav pageContent pageSidebar'",
      gridTemplateRows: 'max-content auto 1fr',
      gridTemplateColumns: 'auto 1fr auto',
      overflowY: 'auto',
      height: '100%',
      '@media print': {
        display: 'block',
        height: 'auto',
        overflowY: 'inherit',
      },
    },
    fullViewport: {
      overflow: 'hidden',
    },
  },
  { name: 'BackstagePage' },
);

export type BackstagePageProps = {
  themeId?: string;
  fullViewport?: boolean;
  children?: ReactNode;
};

// eslint-disable-next-line @spotify-confidence/page-requires-tracking -- Layout wrapper from Backstage, not an actual page
export const BackstagePage = forwardRef<HTMLDivElement, BackstagePageProps>(
  function BackstagePageInner(props: BackstagePageProps, ref) {
    const { themeId = 'other', fullViewport, children } = props;
    const classes = useStyles();

    const theme = useMemo(
      () => (baseTheme: Theme) => ({
        ...baseTheme,
        page: baseTheme.getPageTheme({ themeId }),
      }),
      [themeId],
    );

    return (
      <ThemeProvider theme={theme}>
        <main
          ref={ref}
          className={classNames(classes.root, {
            [classes.fullViewport]: fullViewport,
          })}
        >
          {children}
        </main>
      </ThemeProvider>
    );
  },
);
